import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import type { User } from '../types';
import * as authService from '../services/authService';
import { auth } from '../src/firebase';

type ModalView = 'none' | 'login' | 'signup' | 'forgotPassword' | 'resetEmailSent';

interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  modalView: ModalView;
  setModalView: React.Dispatch<React.SetStateAction<ModalView>>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modalView, setModalView] = useState<ModalView>('none');
  
  // Detect if the app is being run by react-snap for pre-rendering.
  const isPrerendering = navigator.userAgent.includes('ReactSnap');

  useEffect(() => {
    // If we're pre-rendering, we don't need to authenticate with Firebase.
    // Set a default state and finish loading immediately.
    if (isPrerendering) {
      setIsLoading(false);
      setUser(null);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in, get their profile from Firestore.
        let userProfile = await authService.getUserProfile(firebaseUser.uid);

        // FIX: If a user is authenticated but has no Firestore profile, create one on-the-fly.
        // This handles existing users from v1 logging into v2 for the first time.
        if (!userProfile) {
          console.log(`User profile for ${firebaseUser.uid} not found. Creating a new one.`);
          userProfile = await authService.createProfileForExistingUser(firebaseUser);
        }
        
        setUser(userProfile);
      } else {
        // User is signed out.
        setUser(null);
      }
      setIsLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [isPrerendering]);

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    // onAuthStateChanged will handle setting the user state
  };

  const signup = async (name: string, email: string, password: string) => {
    await authService.createUser(name, email, password);
    // onAuthStateChanged will handle setting the user state
  };

  const logout = async () => {
    await signOut(auth);
    // onAuthStateChanged will handle setting the user state to null
  };

  return (
    <AuthContext.Provider value={{ user, setUser, isLoading, login, signup, logout, modalView, setModalView }}>
      {children}
    </AuthContext.Provider>
  );
};